import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead, getProductSchema, getBreadcrumbSchema } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { ProductCard } from '../components/common/ProductCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getProduct, getProducts, getCategory, getBrand, getCategories, getBrands } from '../lib/api';
import { generateWhatsAppLink, getProductWhatsAppMessage } from '../lib/utils';
import { ChevronLeft, ChevronRight, Check, MessageCircle } from 'lucide-react';

export default function ProductPage() {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const prodRes = await getProduct(productSlug);
        setProduct(prodRes.data);

        const [catsRes, brandsRes] = await Promise.all([
          getCategories(),
          getBrands(),
        ]);

        // Find category and brand by ID
        const foundCategory = catsRes.data.find(c => c.id === prodRes.data.category_id);
        const foundBrand = brandsRes.data.find(b => b.id === prodRes.data.brand_id);

        setCategory(foundCategory);
        setBrand(foundBrand);
        setCategories(catsRes.data);
        setBrands(brandsRes.data);

        // Get related products from same category
        const relatedRes = await getProducts({ category_id: prodRes.data.category_id, limit: 4 });
        setRelatedProducts(relatedRes.data.filter(p => p.id !== prodRes.data.id).slice(0, 4));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productSlug]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="skeleton aspect-square" />
            <div className="space-y-4">
              <div className="skeleton h-8 w-3/4" />
              <div className="skeleton h-4 w-1/2" />
              <div className="skeleton h-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom text-center py-20">
          <h1 className="heading-lg mb-4">Product Not Found</h1>
          <Link to="/products" className="btn-primary">Back to Products</Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1541643600914-78b084683601?w=800'];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const whatsappMessage = getProductWhatsAppMessage(product, category?.name, brand?.name);
  const whatsappLink = generateWhatsAppLink(whatsappMessage);

  const breadcrumbs = [
    { label: 'Products', href: '/products' },
    ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
    ...(brand && category ? [{ label: brand.name, href: `/category/${category.slug}/brand/${brand.slug}` }] : []),
    { label: product.name },
  ];

  // Schema.org data with Product and BreadcrumbList
  const breadcrumbSchemaItems = [
    { name: 'Home', url: 'https://passionofaroma.com/' },
    { name: 'Products', url: 'https://passionofaroma.com/products' },
    ...(category ? [{ name: category.name, url: `https://passionofaroma.com/category/${category.slug}` }] : []),
    ...(brand && category ? [{ name: brand.name, url: `https://passionofaroma.com/category/${category.slug}/brand/${brand.slug}` }] : []),
    { name: product.name, url: `https://passionofaroma.com/product/${product.slug}` },
  ];

  const schemaData = [
    getProductSchema(product, category, brand),
    getBreadcrumbSchema(breadcrumbSchemaItems),
  ];

  return (
    <>
      <SEOHead
        title={`${product.name}${brand ? ` by ${brand.name}` : ''}`}
        description={product.description || `Premium ${product.product_type || 'fragrance'} from ${brand?.name || 'top brands'}. Available for wholesale.`}
        canonical={`https://passionofaroma.com/product/${product.slug}`}
        ogImage={images[0]}
        ogType="product"
        schemaData={schemaData}
      />

      <div className="pt-24 bg-white" data-testid="product-page">
        <div className="container-custom py-4">
          <Breadcrumbs items={breadcrumbs} />
        </div>

        {/* Product Details */}
        <section className="container-custom pb-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Images */}
            <div className="relative">
              <div className="aspect-square bg-[#F7F6F3] overflow-hidden">
                <img
                  src={images[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>

              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    data-testid="prev-image"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    data-testid="next-image"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>

                  {/* Thumbnails */}
                  <div className="flex gap-2 mt-4">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-20 h-20 border-2 overflow-hidden ${
                          index === currentImageIndex ? 'border-[#D4AF37]' : 'border-transparent'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Info */}
            <div>
              {/* Brand & Category */}
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="label-text hover:underline"
                  >
                    {category.name}
                  </Link>
                )}
                {category && brand && <span className="text-gray-300">|</span>}
                {brand && category && (
                  <Link
                    to={`/category/${category.slug}/brand/${brand.slug}`}
                    className="text-xs uppercase tracking-widest text-gray-500 hover:text-[#D4AF37]"
                  >
                    {brand.name}
                  </Link>
                )}
              </div>

              <h1 className="heading-lg mb-4" data-testid="product-name">{product.name}</h1>

              {/* Availability */}
              <div className="flex items-center gap-2 mb-6">
                <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm ${
                  product.availability === 'In Stock' 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-amber-50 text-amber-700'
                }`}>
                  {product.availability === 'In Stock' && <Check className="h-4 w-4" />}
                  {product.availability}
                </span>
              </div>

              <div className="gold-separator mb-6" />

              {/* Description */}
              {(product.short_description || product.full_description || product.description) && (
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {product.full_description || product.short_description || product.description}
                </p>
              )}

              {/* Attributes */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {(product.volume_ml || product.volume) && (
                  <div className="p-4 bg-[#F7F6F3]">
                    <span className="label-text block mb-1">Volume</span>
                    <span className="text-[#0B1F3B] font-medium">{product.volume_ml || product.volume}</span>
                  </div>
                )}
                {product.product_type && (
                  <div className="p-4 bg-[#F7F6F3]">
                    <span className="label-text block mb-1">Type</span>
                    <span className="text-[#0B1F3B] font-medium">{product.product_type}</span>
                  </div>
                )}
                {product.moq && (
                  <div className="p-4 bg-[#F7F6F3]">
                    <span className="label-text block mb-1">MOQ</span>
                    <span className="text-[#0B1F3B] font-medium">{product.moq}</span>
                  </div>
                )}
              </div>

              {/* Fragrance Notes - supports both new schema (notes_top, etc) and old schema (key_notes) */}
              {(product.notes_top || product.notes_middle || product.notes_base || product.key_notes) && (
                <div className="mb-8">
                  <span className="label-text block mb-3">Fragrance Notes</span>
                  {product.key_notes ? (
                    <p className="text-gray-600 italic">{product.key_notes}</p>
                  ) : (
                    <div className="space-y-2 text-gray-600">
                      {product.notes_top && (
                        <p><span className="font-medium text-[#0B1F3B]">Top:</span> {product.notes_top}</p>
                      )}
                      {product.notes_middle && (
                        <p><span className="font-medium text-[#0B1F3B]">Heart:</span> {product.notes_middle}</p>
                      )}
                      {product.notes_base && (
                        <p><span className="font-medium text-[#0B1F3B]">Base:</span> {product.notes_base}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowQuoteForm(true)}
                  className="btn-primary flex-1 min-w-[200px]"
                  data-testid="product-quote-btn"
                >
                  Request Quote
                </button>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center justify-center gap-2 flex-1 min-w-[200px]"
                  data-testid="product-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4" />
                  Enquire on WhatsApp
                </a>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-8 border-t border-gray-100">
                <div className="flex flex-wrap gap-6">
                  {['100% Authentic', 'Wholesale Pricing', 'Fast Shipping'].map((badge) => (
                    <span key={badge} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-[#D4AF37]" />
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="py-16 bg-[#F7F6F3]" data-testid="related-products">
            <div className="container-custom">
              <h2 className="heading-md mb-8">Related Products</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {relatedProducts.map((prod) => {
                  const prodCategory = categories.find(c => c.id === prod.category_id);
                  const prodBrand = brands.find(b => b.id === prod.brand_id);
                  return (
                    <ProductCard
                      key={prod.id}
                      product={prod}
                      category={prodCategory?.name}
                      categorySlug={prodCategory?.slug}
                      brand={prodBrand?.name}
                      brandSlug={prodBrand?.slug}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl={`/product/${product.slug}`}
        interestCategoryId={category?.id}
        interestBrandId={brand?.id}
        interestProductId={product.id}
        showBuyerType={true}
        title={`Request Quote for ${product.name}`}
        subtitle="Get wholesale pricing for this product"
      />
    </>
  );
}
